/**
 * O editor de um texto livre.
 *
 * A tela é uma folha só: o título em cima, a barra de ações (salvar, baixar em
 * `.docx`, imprimir em PDF, apresentar) e o editor ocupando o resto. As escolhas
 * de leitura — fonte, corpo e largura da coluna — ficam num painel discreto, para
 * não competir com o texto.
 *
 * Duas saídas, e a diferença importa:
 *
 * - **`.docx`** é para continuar editando em outro programa (o Word, o LibreOffice,
 *   o celular). Sai com a fonte e o corpo escolhidos.
 * - **PDF** é para entregar. Sai pela impressão do navegador, com a mesma coluna e
 *   a mesma fonte da tela — o que a pessoa vê é o que sai.
 *
 * E a terceira porta, **Apresentar**, não baixa nada: abre o texto em corpo grande
 * para ler na tela, inclusive no celular.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Check,
  Download,
  Loader2,
  PenLine,
  Presentation,
  Printer,
  Settings2,
  TriangleAlert,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Campo } from '@/components/ui/label';
import { api, ErroApi, type TextoCompleto } from '@/lib/api';
import { baixarTextoDocx } from '@/texto/docxDoTexto';
import { EditorLivre } from '@/texto/EditorLivre';
import type { EditorLivreHandle } from '@/texto/EditorLivre';
import { CompartilharTexto } from '@/texto/CompartilharTexto';
import { InserirBiblia } from '@/texto/InserirBiblia';
import {
  CORPO_MAXIMO_PT,
  CORPO_MINIMO_PT,
  DESCRICAO_FONTE_DO_TEXTO,
  FAMILIA_DA_FONTE,
  FONTES_DO_TEXTO,
  LARGURA_DA_TELA,
  LARGURA_MAXIMA_MM,
  LARGURA_MINIMA_MM,
  ROTULO_FONTE_DO_TEXTO,
  contarPalavras,
  formatoDoTexto,
} from '@miolos/core';
import type { FormatoDoTexto } from '@miolos/core';

export function Texto() {
  const { id = '' } = useParams();
  const navegar = useNavigate();

  const [texto, setTexto] = useState<TextoCompleto | null>(null);
  const [titulo, setTitulo] = useState('');
  const [conteudo, setConteudo] = useState('');
  const [formato, setFormato] = useState<FormatoDoTexto>(formatoDoTexto(null));
  const [versao, setVersao] = useState(0);
  const [naoSalvo, setNaoSalvo] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [salvo, setSalvo] = useState(false);
  const [painel, setPainel] = useState(false);
  const [imprimindo, setImprimindo] = useState(false);
  const [erro, setErro] = useState('');
  /** Sou o dono? Só o dono compartilha, apaga e vê quem tem acesso. */
  const [meu, setMeu] = useState(true);
  /** Texto compartilhado só para leitura: a barra some e o texto não aceita tecla. */
  const [podeEditar, setPodeEditar] = useState(true);
  const editorRef = useRef<EditorLivreHandle | null>(null);

  const estado = useRef({ titulo: '', conteudo: '', formato: formatoDoTexto(null) });
  estado.current = { titulo, conteudo, formato };

  const carregar = useCallback(async () => {
    try {
      const { texto: aberto, meu: dono, podeEditar: editar } = await api.abrirTexto(id);
      setTexto(aberto);
      setTitulo(aberto.titulo);
      setConteudo(aberto.conteudo);
      setFormato(formatoDoTexto(aberto.formato));
      setMeu(dono);
      setPodeEditar(editar);
      setVersao((atual) => atual + 1);
      setNaoSalvo(false);
      setErro('');
    } catch (causa) {
      setErro(causa instanceof ErroApi ? causa.message : 'Não foi possível abrir este texto.');
    }
  }, [id]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  const salvar = useCallback(async () => {
    setSalvando(true);
    try {
      await api.salvarTexto(id, {
        titulo: estado.current.titulo.trim() || 'Texto sem título',
        conteudo: estado.current.conteudo,
        formato: estado.current.formato,
      });
      setNaoSalvo(false);
      setSalvo(true);
      window.setTimeout(() => setSalvo(false), 1800);
    } catch (causa) {
      setErro(causa instanceof ErroApi ? causa.message : 'Não foi possível salvar.');
    } finally {
      setSalvando(false);
    }
  }, [id]);

  /* Ctrl+S na página (dentro do editor, quem trata é o TinyMCE). */
  useEffect(() => {
    const aoTeclar = (evento: KeyboardEvent) => {
      if ((evento.ctrlKey || evento.metaKey) && evento.key.toLowerCase() === 's') {
        evento.preventDefault();
        void salvar();
      }
    };
    document.addEventListener('keydown', aoTeclar);
    return () => document.removeEventListener('keydown', aoTeclar);
  }, [salvar]);

  const baixarDocx = async () => {
    try {
      await baixarTextoDocx(conteudo, titulo.trim() || 'Texto', formato);
    } catch (causa) {
      setErro(causa instanceof Error ? causa.message : 'Não foi possível gerar o .docx.');
    }
  };

  /*
   * A impressão é feita numa cópia do texto, montada fora do site.
   *
   * O editor vive dentro de um `iframe`, e o navegador não garante imprimir o
   * conteúdo de um iframe. Então, na hora de imprimir, o mesmo HTML é desenhado
   * na `.area-impressao` — que é a única coisa que a regra global de `@media
   * print` deixa aparecer.
   */
  useEffect(() => {
    if (!imprimindo) return;
    const aoTerminar = () => setImprimindo(false);
    window.addEventListener('afterprint', aoTerminar);
    const relogio = window.setTimeout(() => window.print(), 120);
    return () => {
      window.clearTimeout(relogio);
      window.removeEventListener('afterprint', aoTerminar);
    };
  }, [imprimindo]);

  if (erro && !texto) {
    return (
      <div className="mx-auto max-w-2xl space-y-4 py-8">
        <p className="rounded-md border border-destructive/40 bg-destructive/5 p-4 text-sm text-destructive">{erro}</p>
        <Button asChild variant="contorno">
          <Link to="/textos">
            <ArrowLeft />
            Voltar para meus textos
          </Link>
        </Button>
      </div>
    );
  }

  if (!texto) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center gap-3 text-muted-foreground">
        <Loader2 className="size-5 animate-spin" />
        <span className="text-sm">Abrindo o texto...</span>
      </div>
    );
  }

  const palavras = contarPalavras(conteudo);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <Button asChild variant="fantasma" tamanho="pequeno">
          <Link to="/textos">
            <ArrowLeft />
            Meus textos
          </Link>
        </Button>

        <div className="ml-auto flex flex-wrap items-center gap-2">
          <span className="text-[11px] text-muted-foreground">
            {palavras.toLocaleString('pt-BR')} palavra(s)
            {naoSalvo ? ' · não salvo' : null}
          </span>
          <Button variant="contorno" tamanho="pequeno" onClick={() => void salvar()} disabled={salvando || !podeEditar}>
            {salvando ? <Loader2 className="animate-spin" /> : salvo ? <Check /> : <PenLine />}
            {salvo ? 'Salvo' : 'Salvar'}
          </Button>
          {meu ? <CompartilharTexto textoId={id} /> : null}
          {podeEditar ? (
            <InserirBiblia aoInserir={(html) => editorRef.current?.inserirHtml(html)} />
          ) : null}
          <Button variant="contorno" tamanho="pequeno" onClick={() => void baixarDocx()}>
            <Download />
            Baixar .docx
          </Button>
          <Button variant="contorno" tamanho="pequeno" onClick={() => setImprimindo(true)}>
            <Printer />
            Imprimir / PDF
          </Button>
          <Button variant="contorno" tamanho="pequeno" onClick={() => setPainel((aberto) => !aberto)}>
            <Settings2 />
            Leitura
          </Button>
          <Button
            tamanho="pequeno"
            onClick={async () => {
              if (naoSalvo && podeEditar) await salvar();
              navegar(`/textos/${id}/apresentar`);
            }}
          >
            <Presentation />
            Apresentar
          </Button>
        </div>
      </div>

      {!podeEditar ? (
        <p className="rounded-md border border-border bg-muted/50 p-3 text-sm leading-relaxed">
          Este texto foi compartilhado com você <strong>só para leitura</strong>. Você pode ler, imprimir e
          baixar — para escrever nele, peça ao dono para dar permissão de edição.
        </p>
      ) : null}

      {erro ? (
        <p className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
          <TriangleAlert className="mt-0.5 size-4 shrink-0" />
          {erro}
        </p>
      ) : null}

      <input
        value={titulo}
        readOnly={!podeEditar}
        onChange={(evento) => {
          setTitulo(evento.target.value);
          setNaoSalvo(true);
        }}
        placeholder="Título do texto"
        className="w-full border-0 bg-transparent text-2xl font-semibold tracking-tight outline-none placeholder:text-muted-foreground/60"
      />

      {painel ? (
        <div className="grid gap-3 rounded-lg border border-border bg-card p-3 sm:grid-cols-3">
          <Campo rotulo="Fonte" dica={DESCRICAO_FONTE_DO_TEXTO[formato.fonte]}>
            <select
              value={formato.fonte}
              onChange={(evento) => {
                setFormato((atual) => ({ ...atual, fonte: evento.target.value as FormatoDoTexto['fonte'] }));
                setNaoSalvo(true);
              }}
              className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
            >
              {FONTES_DO_TEXTO.map((fonte) => (
                <option key={fonte} value={fonte}>
                  {ROTULO_FONTE_DO_TEXTO[fonte]}
                </option>
              ))}
            </select>
          </Campo>

          <Campo rotulo={`Corpo do texto (${formato.corpoPt} pt)`}>
            <input
              type="range"
              min={CORPO_MINIMO_PT}
              max={CORPO_MAXIMO_PT}
              step={1}
              value={formato.corpoPt}
              onChange={(evento) => {
                setFormato((atual) => ({ ...atual, corpoPt: Number(evento.target.value) }));
                setNaoSalvo(true);
              }}
              className="w-full"
            />
          </Campo>

          <Campo
            rotulo={
              formato.larguraMm === LARGURA_DA_TELA
                ? 'Largura: tela toda'
                : `Largura da coluna (${formato.larguraMm} mm)`
            }
            dica={
              formato.larguraMm === LARGURA_DA_TELA
                ? 'O texto ocupa 100% da largura — bom para tabela larga e texto colado de fora.'
                : 'Coluna estreita cansa menos. “Tela toda” usa 100% da largura.'
            }
          >
            <div className="space-y-2">
              <input
                type="range"
                min={LARGURA_MINIMA_MM}
                max={LARGURA_MAXIMA_MM}
                step={10}
                value={formato.larguraMm === LARGURA_DA_TELA ? LARGURA_MINIMA_MM : formato.larguraMm}
                disabled={formato.larguraMm === LARGURA_DA_TELA}
                onChange={(evento) => {
                  setFormato((atual) => ({ ...atual, larguraMm: Number(evento.target.value) }));
                  setNaoSalvo(true);
                }}
                className="w-full"
              />
              <label className="flex items-center gap-2 text-xs">
                <input
                  type="checkbox"
                  checked={formato.larguraMm === LARGURA_DA_TELA}
                  onChange={(evento) => {
                    setFormato((atual) => ({
                      ...atual,
                      larguraMm: evento.target.checked ? LARGURA_DA_TELA : 150,
                    }));
                    setNaoSalvo(true);
                  }}
                />
                Usar a tela toda (100% da largura)
              </label>
            </div>
          </Campo>
        </div>
      ) : null}

      {/*
        A caixa do editor ocupa a largura toda de propósito: a coluna de texto é
        feita **dentro** dele (`estiloDoTexto`), para a barra de ferramentas não
        ficar espremida e esconder botões atrás de um "…".
      */}
      <EditorLivre
        ref={editorRef}
        html={conteudo}
        formato={formato}
        versao={versao}
        somenteLeitura={!podeEditar}
        aoMudar={(html) => {
          setConteudo(html);
          setNaoSalvo(true);
        }}
        aoSalvar={() => void salvar()}
        altura={620}
      />

      <p className="text-[11px] leading-relaxed text-muted-foreground">
        O texto é salvo na sua conta — abra em qualquer aparelho. <strong>Baixar .docx</strong> leva o texto
        para o Word (imagens não vão junto nesta versão); <strong>Imprimir / PDF</strong> gera o arquivo com
        a mesma coluna e a mesma fonte desta tela.
      </p>

      {imprimindo && typeof document !== 'undefined'
        ? createPortal(
            <div className="area-impressao">
              <style>{`@page { size: A4; margin: 20mm; }`}</style>
              <article
                style={{
                  fontFamily: FAMILIA_DA_FONTE[formato.fonte],
                  fontSize: `${formato.corpoPt}pt`,
                  lineHeight: 1.6,
                  maxWidth: formato.larguraMm === LARGURA_DA_TELA ? 'none' : `${formato.larguraMm}mm`,
                  margin: '0 auto',
                }}
                dangerouslySetInnerHTML={{ __html: conteudo }}
              />
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}
